/**
 * Email verification page for Aegix One.
 *
 * Two modes:
 * 1. Pending: tells user to check their email for a verification link
 * 2. Verification link: validates the key from the email link
 */

import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Text,
  Image,
  VStack,
  Link,
} from '@chakra-ui/react';
import { Button } from '@themes/aegix';
import { useColorMode } from '@/components/ui/color-mode';
import logoLight from '@/assets/aegix-one-logo-light.svg';
import logoDark from '@/assets/aegix-one-logo-dark.svg';
import { useVerifyEmail } from '@/hooks/useVerifyEmail';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function VerifyEmail() {
  const { t } = useTranslation();
  useDocumentTitle(t('verifyEmail.title'));
  const { colorMode } = useColorMode();
  const { emailFromState, status, error } = useVerifyEmail();

  return (
    <Flex
      height="100vh"
      alignItems="center"
      justifyContent="center"
      bg="surface.primary"
    >
      <Box
        maxW="400px"
        w="100%"
        p={8}
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        bg="surface.primary"
        boxShadow="0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)"
      >
        <VStack gap={6}>
          <Image
            src={colorMode === 'dark' ? logoDark : logoLight}
            alt="Aegix One"
            w="200px"
          />

          {status === 'pending' && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="text.primary">
                {t('verifyEmail.checkEmailTitle')}
              </Text>
              <Text
                fontSize="sm"
                color="text.secondary"
                textAlign="center"
              >
                {t('verifyEmail.checkEmailDesc', { email: emailFromState || t('verifyEmail.fallbackEmail') })}
              </Text>

              <Text fontSize="sm" color="text.secondary" textAlign="center">
                {t('verifyEmail.spamNoticeStart')}{' '}
                <Link asChild color="blue.500" fontWeight="medium">
                  <RouterLink to="/login">{t('verifyEmail.signInAgain')}</RouterLink>
                </Link>{' '}
                {t('verifyEmail.spamNoticeEnd')}
              </Text>
            </>
          )}

          {status === 'verifying' && (
            <Text fontSize="sm" color="text.secondary" textAlign="center">
              {t('verifyEmail.verifying')}
            </Text>
          )}

          {status === 'verified' && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="green.600">
                {t('verifyEmail.verifiedTitle')}
              </Text>
              <Text fontSize="sm" color="text.secondary" textAlign="center">
                {t('verifyEmail.verifiedDesc')}
              </Text>
              <Link asChild>
                <RouterLink to="/login">
                  <Button visual="primary" style={{ width: '100%' }}>
                    {t('verifyEmail.continueSignIn')}
                  </Button>
                </RouterLink>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="red.500">
                {t('verifyEmail.failedTitle')}
              </Text>
              <Text fontSize="sm" color="text.secondary" textAlign="center">
                {error}
              </Text>
            </>
          )}

          <Text fontSize="sm" color="text.secondary">
            <Link asChild color="blue.500" fontWeight="medium">
              <RouterLink to="/login">{t('verifyEmail.backSignIn')}</RouterLink>
            </Link>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
}
